<script setup lang="ts">
import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import AuthForm from '@/components/form/AuthForm.vue';
import AuthFormWrapper from '@/components/auth/AuthFormWrapper.vue';
import Button from '@/components/button/Button.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';
import PasswordRequirementsList from '@/components/passwordRequirementsList/PasswordRequirementsList.vue';
import PasswordStrength from '@/components/passwordStrength/PasswordStrength.vue';

import StackedFormFooter from '@/components/formFooter/StackedFormFooter.vue';

const props = defineProps<{
  email: string;
  token: string;
}>();

const resetPassword = useForm({
  method: 'POST',
  url: route('password.update'),
  fields: {
    email: props.email,
    token: props.token,
    password: '',
    password_confirmation: ''
  }
});

const passwordScore = ref(0);

const isPasswordLongEnough = computed(() => resetPassword.fields.password.length >= 8);
const doPasswordsMatch = computed(
  () =>
    resetPassword.fields.password.length > 0 &&
    resetPassword.fields.password === resetPassword.fields.password_confirmation
);

function handleScoreChange(score: number) {
  passwordScore.value = score;
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/domains/auth/layouts/one-column-guest.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <AuthFormWrapper
    textAlignment="center"
    title="Set a new password"
    description="Please enter a new password for your account"
  >
    <AuthForm @submit.prevent="resetPassword.submit">
      <FormTextInput
        label="Password"
        name="password"
        autofocus
        v-model="resetPassword.fields.password"
        type="password"
        placeholder="New Password"
        :error="resetPassword.errors.password"
      >
        <template #helperText>
          <PasswordStrength :password="resetPassword.fields.password" @score="handleScoreChange" />
        </template>
      </FormTextInput>

      <FormTextInput
        label="Confirm Password"
        name="password_confirmation"
        v-model="resetPassword.fields.password_confirmation"
        type="password"
        placeholder="Confirm New Password"
      >
        <template #helperText>
          <PasswordStrength
            :password="resetPassword.fields.password_confirmation"
            @score="handleScoreChange"
          />
        </template>
      </FormTextInput>

      <PasswordRequirementsList
        :isPasswordLongEnough="isPasswordLongEnough"
        :doPasswordsMatch="!!doPasswordsMatch"
        :passwordScore="passwordScore"
        class="pt-6"
      />

      <template #footer>
        <StackedFormFooter>
          <Button
            :color="ButtonColor.primary"
            :isDisabled="resetPassword.processing"
            :isLoading="resetPassword.processing"
            :size="ButtonSize.lg"
            :variant="ButtonVariant.solid"
            type="submit"
            useMediaQuery
            >Reset Password</Button
          >
          <Button
            :color="ButtonColor.slate"
            :href="route('login.show')"
            :size="ButtonSize.lg"
            :variant="ButtonVariant.invisible"
            useMediaQuery
            >Back to login</Button
          >
        </StackedFormFooter>
      </template>
    </AuthForm>
  </AuthFormWrapper>
</template>
